import { defineComponent as _defineComponent } from 'vue'
import {computed, onMounted, onUnmounted, ref, watch} from 'vue';
import useClickOutside from '@/composables/useClickOutside';

import PopupContainer from "@/components/popup.vue";
import InvalidDeletePopup from '@/components/InvalidDeletePopup.vue';
import AddParentBuilder from "./add-parent-builder.vue";
import { type DataInstance, Builders, BuilderRecord, checkIfDeletionisValid } from '@busy-human/hxp-library';
import { useShoppingBuilderStore } from '@/store/builder-shopping';
import { useAuth } from '@/store/auth';
import { useCartStore } from '@/store/cart';
import toolTip from '@/components/tool-tip.vue';
import SimpleYesNoModal from '@/components/SimpleYesNoModal.vue';
import { useZohoStrings } from "@/store/zohoStrings";
import router from '@/router';

enum editState {
	EDIT_BUILDER = 0,
	CREATE_PARENT = 1,
}

type Emits = {
	(e: 'close'): void
}

export default /*#__PURE__*/_defineComponent({
  __name: 'edit-builder',
  emits: ["close"],
  setup(__props, { expose: __expose, emit: __emit }) {
  __expose();

const auth = useAuth();
const builderStore = useShoppingBuilderStore();
const cartStore = useCartStore();
const zohoStrings = useZohoStrings();

const showEditBuilderPopup = ref(false);
const showParentBuilderPopup = ref(false);

const showInvalidDeletionPopup = ref(false);
const showCleanupPopup = ref(false);

const state = ref(editState.EDIT_BUILDER);

// type Props = {
// }
// const props = defineProps<Props>();

const emit = __emit;

const whyCantIEditDetailsTitleHtml = computed(() => zohoStrings.strings?.zh_whyCantIEditTitle?.rendered || 'Why cant I edit some details?');
const whyCantIEditSubTitleHtml = computed(() => zohoStrings.strings?.zh_whyCantIEditSubtitle?.rendered || 'You need to clear your cart before editing some details');
const whyCantIEditBodyHtml = computed(() => zohoStrings.strings?.zh_whyCantIEditDescription?.rendered || '');


const workingBuilder = ref<Partial<Builders.Model>>({...Builders.Default(), gender: undefined});
const bringingParent = ref<'Yes' | 'Maybe'| 'No' | null>(null);

const userCart = computed(() => cartStore.carts?.[cartStore.getCartId() || ''] || []);


watch( () => builderStore.selectedBuilderId, () => {
	workingBuilder.value = {...{...Builders.Default(), gender: undefined}, ...builderStore.selectedBuilder};
}, {immediate: true});

const builderType = computed(() => (workingBuilder.value.builderType === Builders.BuilderType.Builder ? 'Teen' : 'Parent'));
const builderRecord = computed(() => builderStore.selectedBuilderRecord);

const builderBirthday = computed({
	get() {
		if(!workingBuilder.value?.birthday) return undefined;
		return workingBuilder.value.birthday;
	},
	set(val) {
		if(!val) {delete workingBuilder.value?.birthday;}
		else {
			const tmp = new Date(val);
			if(tmp instanceof Date && !isNaN(tmp.valueOf())) {
				workingBuilder.value.birthday = Builders.dateToBirthdate(tmp);
			}
		}
	}
});

const birthdayValid = computed<'valid' | 'tooYoung' | 'tooOld' | 'invalid'>(() => {
	if(!builderBirthday.value) return 'invalid';

	const builderAge = Builders.GetBuilderAge(builderBirthday.value);
	if(workingBuilder.value.builderType === Builders.BuilderType.Builder){
		if(builderAge >= Builders.AdultAge ) return 'tooOld';
		if(builderAge < (Builders.MinimumAge-1) ) return 'tooYoung';
	}else if(builderAge < (Builders.AdultAge-1) ) {return 'tooYoung';}
	return 'valid';
});

const resolvedBringingParent = computed<'Yes' | 'Maybe'| 'No'>(() => {
	if(!bringingParent.value || workingBuilder.value.builderType !== Builders.BuilderType.Builder) return 'No';
	if(birthdayValid.value === 'valid' || birthdayValid.value === 'invalid'){
		return bringingParent.value;
	}else{
		return 'No';
	}
});

onMounted(()=>{
	if(workingBuilder.value.builderType === Builders.BuilderType.Builder){
		if(builderRecord.value?.builderTypeDetails?.goingAsPair){
			bringingParent.value = 'Yes';
		}else if(builderRecord.value?.builderTypeDetails.maybePair){
			bringingParent.value = 'Maybe';
		}else{
			bringingParent.value = 'No';
		}
	}
});

// const parentBuilderFor = ref<boolean | string | null>(true);

// //TODO: Change Select into popup to fit designs
// const handleTeenSelect = (e: any) => {
//     // e.preventDefault()
// };

// const showDropdown = ref(false);
// const teenSelect = ref(null);
// useClickOutside(teenSelect, ()=> {
//     if(showDropdown.value)
//     {showDropdown.value = false;}
// });

async function saveParentBuilder(parentBuilder: Partial<Builders.Model>){
	if(!builderStore.selectedBuilderId) return;
	const teenId = builderStore.selectedBuilderId;
	const parentId = builderStore.addBuilder(false);
	Object.assign(builderStore.builders[parentId], parentBuilder);

	//Link the teen/parent
	builderStore.setBuilderPair(teenId, parentId);

	//Save the parent
	if(auth.isAuthenticated) await builderStore.saveBuilder(parentId);

	builderStore.selectBuilder(teenId);
	await updateBuilder();
}
async function updateBuilder(){
	if(!builderStore.selectedBuilderId) return;
	if(builderStore.selectedBuilderRecord?.builderTypeDetails.goingAsPair && bringingParent.value !== 'Yes'
		&& workingBuilder.value.builderType === Builders.BuilderType.Builder && !builderStore.selectedGroup?.parent) {
		builderStore.selectedBuilderRecord.builderTypeDetails.goingAsPair = false;
		builderStore.selectedBuilderRecord.builderTypeDetails.pairUser = null;
	}
	builderStore.builders[builderStore.selectedBuilderId] = {...builderStore.builders[builderStore.selectedBuilderId], ...workingBuilder.value};
	if(workingBuilder.value.builderType === Builders.BuilderType.Builder && !builderStore.selectedGroup?.parent) builderStore.setMaybeParent(builderStore.selectedBuilderId, bringingParent.value === 'Maybe');
	await builderStore.saveBuilder();
	emit('close');
}

const showDeleteTeen = ref(false);
const showDeletePairTeen = ref(false);
const showDeleteParent = ref(false);

function deleteBuilder(){

	if(!builderStore.selectedBuilderId) return;

	const builder = builderStore.builders[builderStore.selectedBuilderId];
	const rec = builderStore.builderRecords[builderStore.selectedBuilderId];

	const isDeleteValid = checkIfDeletionisValid(rec);
	if(isDeleteValid === 'contact-hxp'){
		showInvalidDeletionPopup.value = true;
	}else if(isDeleteValid === 'cleanup'){
		showCleanupPopup.value = true;
	}else if(isDeleteValid === 'valid'){
		if(builder.builderType !== Builders.BuilderType.Builder){
			showDeleteParent.value = true;
		}else if(rec.builderTypeDetails?.goingAsPair){
			showDeletePairTeen.value = true;
		}else{
			showDeleteTeen.value = true;
		}
	}
}

async function confirmDeleteBuilder(){
	if(!builderStore.selectedBuilderId) return;

	if(showDeletePairTeen.value){
		showDeletePairTeen.value = false;
		const parentId = builderStore.builderRecords[builderStore.selectedBuilderId].builderTypeDetails?.pairUser;
		if(parentId)
		{await builderStore.deleteBuilder(parentId);}
	}else if(showDeleteParent.value){
		showDeleteParent.value = false;
		const teenId = builderStore.builderRecords[builderStore.selectedBuilderId].builderTypeDetails?.pairUser;
		const teen = builderStore.builderRecords[teenId || ''];
		if(teenId && teen){
			teen.builderTypeDetails = {
				goingAsPair: false,
				goingOnTrip: true,
				pairUser: null
			};
			cartStore.clearCart(teenId);

			builderStore.saveBuilder(teenId);
		}
	}

	showDeleteTeen.value = false;

	await builderStore.deleteBuilder(builderStore.selectedBuilderId);

	emit('close');
}

function handleClick(){
	if(userCart.value.length > 0){
		// showClearCart.value = true;
	}
}

const isBoxScrollable = ref(false);
const builderDetailsElement = ref<HTMLElement | null>(null);
function checkDetailsScrollable() {
	if(!builderDetailsElement.value) {
		isBoxScrollable.value = false;
		return;
	}

	const el = builderDetailsElement.value;
	const canScroll = el.scrollHeight > el.clientHeight;
	const CLOSEDIST = 5;
	const scrolledDown = el.scrollTop > ((el.scrollHeight - el.offsetHeight) - CLOSEDIST);
	isBoxScrollable.value = (canScroll && !scrolledDown);
}

function goToCart(){
	emit('close');
	showEditBuilderPopup.value = false;
	router.push({name:'Shopping Cart'});
}

function handleRadioSelect(selectedOption:string, event: any){
	if(selectedOption === 'Yes' && !builderRecord.value?.builderTypeDetails?.goingAsPair && userCart.value.length > 0){
		console.log("Event", event);
		event.preventDefault();
		showParentBuilderPopup.value = true;
	}
}

onMounted(() => {
	checkDetailsScrollable();
	window.addEventListener('resize', checkDetailsScrollable);
});
onUnmounted(() => {
	window.removeEventListener('resize', checkDetailsScrollable);
});


const __returned__ = { auth, builderStore, cartStore, zohoStrings, showEditBuilderPopup, showParentBuilderPopup, showInvalidDeletionPopup, showCleanupPopup, editState, state, emit, whyCantIEditDetailsTitleHtml, whyCantIEditSubTitleHtml, whyCantIEditBodyHtml, workingBuilder, bringingParent, userCart, builderType, builderRecord, builderBirthday, birthdayValid, resolvedBringingParent, saveParentBuilder, updateBuilder, showDeleteTeen, showDeletePairTeen, showDeleteParent, deleteBuilder, confirmDeleteBuilder, handleClick, isBoxScrollable, builderDetailsElement, checkDetailsScrollable, goToCart, handleRadioSelect, computed, onMounted, onUnmounted, ref, watch, get useClickOutside() { return useClickOutside }, PopupContainer, InvalidDeletePopup, AddParentBuilder, get Builders() { return Builders }, get BuilderRecord() { return BuilderRecord }, get checkIfDeletionisValid() { return checkIfDeletionisValid }, get useShoppingBuilderStore() { return useShoppingBuilderStore }, get useAuth() { return useAuth }, get useCartStore() { return useCartStore }, toolTip, SimpleYesNoModal, get useZohoStrings() { return useZohoStrings }, get router() { return router } }
Object.defineProperty(__returned__, '__isScriptSetup', { enumerable: false, value: true })
return __returned__
}

})